/**
 * BulkImportDialog — three-step wizard for onboarding many workers at once.
 *
 * Step 1  Upload      Manager downloads the CSV template and picks a file.
 * Step 2  Review      Preview endpoint returns valid / duplicate / invalid rows;
 *                     the manager can see per-row reasons before committing.
 * Step 3  Send invites  After confirm, invitations are NOT auto-sent — the
 *                     manager clicks "Send invite" per row or "Send all".
 *
 * Reused by both `/team` (TeamMembers page) and `/workers` (ManagerDashboard).
 */
import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Download, X, CheckCircle2, AlertCircle, Mail } from "lucide-react";

export default function BulkImportDialog({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [step, setStep] = useState("upload"); // upload | preview | done
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState({});
  const [invited, setInvited] = useState({});
  const [bulkSending, setBulkSending] = useState(false);

  const reset = () => {
    setFile(null);
    setStep("upload");
    setBusy(false);
    setPreview(null);
    setResult(null);
    setSending({});
    setInvited({});
    setBulkSending(false);
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/team/workers-import/template.csv", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "audentra-workers-template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download template", { description: e.message });
    }
  };

  const runPreview = async () => {
    if (!file) {
      toast.error("Choose a CSV file first");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/team/workers-import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setStep("preview");
    } catch (e) {
      toast.error("Could not read CSV", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  const confirmImport = async () => {
    if (!preview?.valid?.length) {
      toast.error("No valid rows to import");
      return;
    }
    setBusy(true);
    try {
      const rows = preview.valid.map((r) => ({
        first_name: r.first_name,
        last_name: r.last_name,
        email: r.email,
        worker_role: r.worker_role,
        telephone: r.telephone || undefined,
      }));
      const { data } = await api.post("/team/workers-import/confirm", { rows });
      setResult(data);
      setStep("done");
      toast.success(`${data.created_count} worker${data.created_count === 1 ? "" : "s"} imported`, {
        description: "No invitation emails were sent. Send them when you're ready below.",
      });
      onImported && onImported();
    } catch (e) {
      toast.error("Import failed", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  const sendInvite = async (u) => {
    setSending((s) => ({ ...s, [u.id]: true }));
    try {
      await api.post(`/team/pending/${u.id}/resend`);
      setInvited((s) => ({ ...s, [u.id]: true }));
      toast.success(`Invitation sent to ${u.email}`);
      onImported && onImported();
    } catch (e) {
      toast.error("Could not send invite", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setSending((s) => ({ ...s, [u.id]: false }));
    }
  };

  const sendAllInvites = async () => {
    const pending = (result?.created || []).filter((u) => !invited[u.id]);
    if (pending.length === 0) return;
    setBulkSending(true);
    let sent = 0;
    for (const u of pending) {
      setSending((s) => ({ ...s, [u.id]: true }));
      try {
        await api.post(`/team/pending/${u.id}/resend`);
        setInvited((s) => ({ ...s, [u.id]: true }));
        sent += 1;
      } catch (_e) {
        /* keep going */
      } finally {
        setSending((s) => ({ ...s, [u.id]: false }));
      }
    }
    setBulkSending(false);
    if (sent > 0) {
      toast.success(`${sent} invitation${sent === 1 ? "" : "s"} sent`);
      onImported && onImported();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-white sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="bulk-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Bulk import workers</DialogTitle>
          <DialogDescription>
            Upload a CSV of new workers, review the parsed results, then confirm the import.
            No invitation emails are sent automatically — you choose when to invite each person.
          </DialogDescription>
        </DialogHeader>

        <BulkImportStepper step={step} />

        {step === "upload" && (
          <div className="space-y-4 mt-1">
            <div className="rounded-md border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-900" data-testid="bulk-template-panel">
              <div className="font-semibold">CSV columns</div>
              <div className="mt-1 text-xs text-teal-800 space-y-0.5">
                <div><span className="font-mono">First Name</span> · <span className="font-mono">Last Name</span> · <span className="font-mono">Email Address</span> · <span className="font-mono">Role</span> · <span className="font-mono">Telephone Number</span> <span className="italic">(optional)</span></div>
                <div className="text-teal-700">
                  Role must be one of: Healthcare Assistant · Senior Care Assistant · Registered Nurse · Chef · Kitchen Assistant.
                </div>
              </div>
              <button
                type="button"
                onClick={downloadTemplate}
                data-testid="bulk-download-template"
                className="mt-2 inline-flex items-center gap-1 text-teal-800 hover:text-teal-900 text-xs font-semibold underline underline-offset-2"
              >
                <Download className="h-3 w-3" /> Download CSV template
              </button>
            </div>

            <label className="flex items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 cursor-pointer h-32 text-center px-4">
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="bulk-file-input"
                className="hidden"
              />
              <div className="text-sm text-slate-600">
                {file ? (
                  <>
                    <div className="font-semibold text-slate-900">{file.name}</div>
                    <div className="text-xs text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB — click to change</div>
                  </>
                ) : (
                  <>
                    <Upload className="h-5 w-5 mx-auto text-slate-400" />
                    <div className="mt-1">Click to choose a CSV file</div>
                  </>
                )}
              </div>
            </label>
          </div>
        )}

        {step === "preview" && preview && (
          <div className="space-y-4 mt-1" data-testid="bulk-preview">
            <div className="grid grid-cols-3 gap-3">
              <StatChip
                testid="preview-stat-valid"
                tone="emerald"
                icon={CheckCircle2}
                label="Ready to import"
                value={preview.totals.will_import}
              />
              <StatChip
                testid="preview-stat-duplicates"
                tone="amber"
                icon={AlertCircle}
                label="Already in Audentra"
                value={preview.totals.duplicates}
              />
              <StatChip
                testid="preview-stat-invalid"
                tone="rose"
                icon={X}
                label="Invalid rows"
                value={preview.totals.invalid}
              />
            </div>

            {preview.valid.length > 0 && (
              <PreviewSection
                testid="preview-valid"
                title="Ready to import"
                tone="emerald"
                rows={preview.valid.map((r) => ({
                  row: r.row,
                  first_name: r.first_name,
                  last_name: r.last_name,
                  email: r.email,
                  role: r.worker_role_label,
                  telephone: r.telephone || "—",
                }))}
                columns={["Row", "First name", "Last name", "Email", "Role", "Telephone"]}
              />
            )}

            {preview.duplicates.length > 0 && (
              <PreviewSection
                testid="preview-duplicates"
                title="Already in Audentra — skipped"
                tone="amber"
                rows={preview.duplicates.map((r) => ({
                  row: r.row,
                  email: r.raw.email,
                  reason: r.reason,
                }))}
                columns={["Row", "Email", "Reason"]}
              />
            )}

            {preview.invalid.length > 0 && (
              <PreviewSection
                testid="preview-invalid"
                title="Invalid — please fix these rows in your CSV and re-upload"
                tone="rose"
                rows={preview.invalid.map((r) => ({
                  row: r.row,
                  email: r.raw.email || "—",
                  reason: r.reason,
                }))}
                columns={["Row", "Email", "Reason"]}
              />
            )}
          </div>
        )}

        {step === "done" && result && (
          <div className="space-y-4 mt-1" data-testid="bulk-done">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
              <div className="flex items-center gap-2 font-semibold">
                <CheckCircle2 className="h-4 w-4" /> {result.created_count} worker{result.created_count === 1 ? "" : "s"} imported.
              </div>
              <div className="mt-1 text-xs">
                Each has the correct role-based compliance checklist ready. No invitation emails were sent — send them below when you&apos;re ready.
              </div>
            </div>

            {result.created.length > 0 && (
              <div className="rounded-md border border-slate-200 overflow-hidden">
                <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest">
                    Imported workers · send invitations
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={sendAllInvites}
                    disabled={bulkSending || result.created.every((u) => invited[u.id])}
                    data-testid="bulk-send-all"
                    className="h-7 px-3 text-xs"
                  >
                    <Mail className="h-3 w-3 mr-1" />
                    {bulkSending ? "Sending…" : "Send all invitations"}
                  </Button>
                </div>
                <table className="w-full text-xs" data-testid="bulk-created-table">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2">Email</th>
                      <th className="text-left px-3 py-2">Name</th>
                      <th className="text-left px-3 py-2">Role</th>
                      <th className="text-right px-3 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.created.map((u) => (
                      <tr key={u.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-mono text-slate-800">{u.email}</td>
                        <td className="px-3 py-2 text-slate-800">{u.name}</td>
                        <td className="px-3 py-2 text-slate-600">{u.worker_role_label}</td>
                        <td className="px-3 py-2 text-right">
                          {invited[u.id] ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold" data-testid={`bulk-sent-${u.id}`}>
                              <CheckCircle2 className="h-3.5 w-3.5" /> Sent
                            </span>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => sendInvite(u)}
                              disabled={sending[u.id] || bulkSending}
                              data-testid={`bulk-send-invite-${u.id}`}
                              className="h-7 px-3 text-xs"
                            >
                              <Mail className={`h-3 w-3 mr-1 ${sending[u.id] ? "animate-pulse" : ""}`} />
                              {sending[u.id] ? "Sending…" : "Send invite"}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {result.skipped.length > 0 && (
              <div className="rounded-md border border-amber-200 overflow-hidden">
                <div className="bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 uppercase tracking-widest">
                  Skipped during confirmation
                </div>
                <table className="w-full text-xs">
                  <tbody>
                    {result.skipped.map((r, i) => (
                      <tr key={i} className="border-t border-amber-100 bg-amber-50/40">
                        <td className="px-3 py-2 text-slate-800 font-mono">{r.email}</td>
                        <td className="px-3 py-2 text-slate-700">{r.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === "upload" && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                onClick={runPreview}
                disabled={busy || !file}
                className="bg-teal-700 hover:bg-teal-800"
                data-testid="bulk-preview-btn"
              >
                {busy ? "Reading…" : "Preview import"}
              </Button>
            </>
          )}
          {step === "preview" && (
            <>
              <Button
                variant="outline"
                onClick={() => { setPreview(null); setStep("upload"); }}
                data-testid="bulk-back"
              >
                Back
              </Button>
              <Button
                onClick={confirmImport}
                disabled={busy || !preview?.valid?.length}
                className="bg-teal-700 hover:bg-teal-800"
                data-testid="bulk-confirm-btn"
              >
                {busy ? "Importing…" : `Confirm & import ${preview?.valid?.length || 0} worker${preview?.valid?.length === 1 ? "" : "s"}`}
              </Button>
            </>
          )}
          {step === "done" && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkImportStepper({ step }) {
  const steps = [
    { key: "upload", label: "Upload CSV" },
    { key: "preview", label: "Review" },
    { key: "done", label: "Send invites" },
  ];
  const activeIdx = steps.findIndex((s) => s.key === step);
  return (
    <div className="flex items-center gap-2 text-xs" data-testid="bulk-stepper">
      {steps.map((s, i) => (
        <div key={s.key} className="flex items-center gap-2">
          <div
            className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-[0.7rem] font-bold ${
              i < activeIdx
                ? "bg-teal-700 text-white"
                : i === activeIdx
                ? "bg-teal-700 text-white ring-2 ring-teal-200"
                : "bg-slate-200 text-slate-500"
            }`}
          >
            {i < activeIdx ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
          </div>
          <span className={i <= activeIdx ? "font-semibold text-slate-900" : "text-slate-500"}>{s.label}</span>
          {i < steps.length - 1 && <span className="h-px w-6 bg-slate-200" />}
        </div>
      ))}
    </div>
  );
}

function StatChip({ testid, tone, icon: Icon, label, value }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
  };
  return (
    <div className={`rounded-md border px-3 py-2 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function PreviewSection({ testid, title, tone, rows, columns }) {
  const headBg = tone === "emerald" ? "bg-emerald-50 text-emerald-800" : tone === "amber" ? "bg-amber-50 text-amber-800" : "bg-rose-50 text-rose-800";
  const border = tone === "emerald" ? "border-emerald-200" : tone === "amber" ? "border-amber-200" : "border-rose-200";
  return (
    <div className={`rounded-md border ${border} overflow-hidden`} data-testid={testid}>
      <div className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-widest ${headBg}`}>{title}</div>
      <div className="max-h-56 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 sticky top-0">
            <tr>
              {columns.map((c) => (
                <th key={c} className="text-left px-3 py-2 font-semibold">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100">
                {Object.keys(r).map((k) => (
                  <td key={k} className="px-3 py-2 text-slate-800 font-mono">{String(r[k])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
